import {
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';
import {
  BulbOutlined,
  DesktopOutlined,
  CloudServerOutlined,
  ApartmentOutlined,
  SafetyCertificateOutlined,
  BarChartOutlined,
  RocketOutlined,
  FireOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Button, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { InterviewPositionCode } from '@/types/interview';
import { createInterview, getInterviewPositions } from '@/services/interview';
import PointsGetter from '@/components/PointsGetter';
import { allInterviewMessagesState, nextMessageIdState } from '@/store/message';

export interface SelectPositionRef {
  show: (resumeNumber: number) => void;
}

export type PositionItem = {
  id: number;
  name: string;
  description: string;
  color: string;
  gradient: string;
  icon: ReactNode;
};

/** 供流程 Step1 与选岗弹窗复用：将接口岗位列表转为展示项（未知 code 使用 DEFAULT 样式） */
export function mapPositionDTOsToItems(
  list: import('@/types/interview').InterviewPositionDTO[],
): PositionItem[] {
  return list.map((item) => {
    const visual =
      item.code in POSITION_VISUAL_MAP
        ? POSITION_VISUAL_MAP[item.code as InterviewPositionCode]
        : POSITION_VISUAL_MAP.DEFAULT;
    return {
      id: item.positionNumber,
      name: item.name,
      description: item.description,
      color: visual.color,
      gradient: visual.gradient,
      icon: visual.icon,
    };
  });
}

/** 未知岗位 code 时使用的默认样式（与 FRONTEND_DEV 同款） */
const DEFAULT_VISUAL = {
  color: '#6366F1',
  gradient: 'from-indigo-500 to-purple-600',
  icon: <DesktopOutlined />,
} as const;

const POSITION_VISUAL_MAP: Record<
  InterviewPositionCode | 'DEFAULT',
  { color: string; gradient: string; icon: ReactNode }
> = {
  DEFAULT: DEFAULT_VISUAL,
  AI_AGENT_ENGINEER: {
    color: '#0D9488',
    gradient: 'from-teal-500 to-cyan-600',
    icon: <ThunderboltOutlined />,
  },
  FRONTEND_DEV: {
    color: '#6366F1',
    gradient: 'from-indigo-500 to-purple-600',
    icon: <DesktopOutlined />,
  },
  JAVA_DEV: {
    color: '#F97316',
    gradient: 'from-orange-500 to-red-500',
    icon: <CloudServerOutlined />,
  },
  ALGORITHM_DEV: {
    color: '#22C55E',
    gradient: 'from-emerald-500 to-teal-600',
    icon: <ApartmentOutlined />,
  },
  TEST_DEV: {
    color: '#EC4899',
    gradient: 'from-pink-500 to-rose-600',
    icon: <SafetyCertificateOutlined />,
  },
  DATA_DEV: {
    color: '#0EA5E9',
    gradient: 'from-sky-500 to-cyan-600',
    icon: <BarChartOutlined />,
  },
  PRODUCT_MANAGER: {
    color: '#FACC15',
    gradient: 'from-amber-400 to-orange-500',
    icon: <RocketOutlined />,
  },
};

const PAGE_SIZE = 6;

function chunk<T>(arr: T[], size: number): T[][] {
  const pages: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    pages.push(arr.slice(i, i + size));
  }
  return pages;
}

export interface SelectPositionStepProps {
  positions: PositionItem[];
  selectedId?: number;
  onSelect: (id: number) => void;
  loading: boolean;
}

/** 选岗 UI（分页横向滚动），供弹窗与两步流程 Step1 复用 */
export function SelectPositionStep({
  positions,
  selectedId,
  onSelect,
  loading,
}: SelectPositionStepProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pages = chunk(positions, PAGE_SIZE);
  const totalPages = Math.max(1, pages.length);
  const canGoPrev = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;

  const goToPage = (page: number) => {
    const next = Math.max(0, Math.min(page, totalPages - 1));
    setCurrentPage(next);
    scrollContainerRef.current?.scrollTo({
      left: next * (scrollContainerRef.current?.clientWidth ?? 0),
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || pages.length <= 1) return;
    const onScroll = () => {
      const pageWidth = el.clientWidth;
      if (pageWidth <= 0) return;
      const page = Math.round(el.scrollLeft / pageWidth);
      setCurrentPage(Math.min(page, totalPages - 1));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [pages.length, totalPages]);

  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => goToPage(currentPage - 1)}
          disabled={!canGoPrev || loading}
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white/80 text-slate-600 transition hover:bg-white hover:text-indigo-600 disabled:opacity-40"
          aria-label="上一页"
        >
          <span className="text-lg">◀</span>
        </button>
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-x-auto scroll-smooth scrollbar-hide flex snap-x snap-mandatory gap-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {loading ? (
            <div className="grid w-full min-w-full grid-cols-2 gap-3 px-3 py-2 sm:grid-cols-3 sm:gap-4 sm:px-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-2xl border border-white/80 bg-white/60 p-5"
                >
                  <div className="mx-auto mb-3 h-14 w-14 rounded-xl bg-slate-200" />
                  <div className="mx-auto mb-2 h-4 w-24 rounded bg-slate-200" />
                  <div className="mx-auto h-3 w-32 rounded bg-slate-200" />
                </div>
              ))}
            </div>
          ) : pages.length === 0 ? (
            <div className="flex min-w-full items-center justify-center py-8 text-sm text-slate-400">
              暂无可用岗位，请稍后重试
            </div>
          ) : (
            pages.map((pagePositions, pageIndex) => (
              <div
                key={pageIndex}
                className="grid min-w-full flex-shrink-0 snap-center grid-cols-2 gap-3 px-3 py-2 sm:grid-cols-3 sm:gap-4 sm:px-4"
              >
                {pagePositions.map((position, indexInPage) => {
                  const isFirstPosition = pageIndex === 0 && indexInPage === 0;
                  return (
                    <div
                      key={position.id}
                      role="button"
                      tabIndex={0}
                      className={`group relative flex cursor-pointer flex-col items-center gap-3 rounded-2xl border-2 p-5 backdrop-blur-sm transition-all duration-200 max-h-[155px] ${
                        selectedId === position.id
                          ? 'scale-[1.02] border-solid bg-white/90 shadow-xl'
                          : 'border-white/80 bg-white/60 hover:-translate-y-1 hover:bg-white/80 hover:shadow-lg'
                      }`}
                      onClick={() => onSelect(position.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSelect(position.id);
                        }
                      }}
                      style={
                        {
                          borderColor: selectedId === position.id ? position.color : undefined,
                          boxShadow:
                            selectedId === position.id
                              ? `0 20px 40px -12px ${position.color}30`
                              : undefined,
                        } as React.CSSProperties
                      }
                      aria-label={`选择岗位：${position.name}`}
                      aria-selected={selectedId === position.id}
                    >
                      {/* HOT 热门标记 - 仅对第一个岗位显示 */}
                      {isFirstPosition && (
                        <div className="absolute -top-1.5 -left-1.5 z-10 flex items-center gap-0.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-md">
                          <FireOutlined className="text-xs" />
                          <span>HOT</span>
                        </div>
                      )}
                      <div
                        className={`flex h-14 w-14 items-center justify-center rounded-xl text-xl text-white shadow-lg transition-all duration-300 bg-gradient-to-br ${position.gradient} ${
                          selectedId === position.id
                            ? 'scale-110 shadow-xl'
                            : 'group-hover:scale-105 group-hover:shadow-xl'
                        }`}
                        style={{
                          boxShadow:
                            selectedId === position.id
                              ? `0 12px 24px -8px ${position.color}50`
                              : undefined,
                        }}
                      >
                        {position.icon}
                      </div>
                      <div className="text-center">
                        <div className="max-w-[110px] mb-1 text-sm font-semibold text-slate-800 truncate">
                          {position.name}
                        </div>
                      </div>
                      <div
                        className={`absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br shadow-md transition-all duration-300 ${position.gradient} ${
                          selectedId === position.id ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
                        }`}
                      >
                        <svg
                          className="h-3.5 w-3.5 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div
                        className={`pointer-events-none absolute inset-0 rounded-2xl transition-opacity duration-300 ${
                          selectedId === position.id
                            ? 'opacity-100'
                            : 'opacity-0 group-hover:opacity-50'
                        }`}
                        style={{
                          background: `radial-gradient(circle at 50% 50%, ${position.color}10 0%, transparent 70%)`,
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <button
          type="button"
          onClick={() => goToPage(currentPage + 1)}
          disabled={!canGoNext || loading}
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white/80 text-slate-600 transition hover:bg-white hover:text-indigo-600 disabled:opacity-40"
          aria-label="下一页"
        >
          <span className="text-lg">▶</span>
        </button>
      </div>
      {!loading && pages.length > 0 && (
        <div className="mb-6 flex justify-center gap-1.5" role="tablist" aria-label="分页">
          {pages.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goToPage(i)}
              className={`h-2 rounded-full transition-all duration-200 ${
                i === currentPage ? 'w-6 bg-indigo-500' : 'w-2 bg-slate-300 hover:bg-slate-400'
              }`}
              aria-label={`第 ${i + 1} 页`}
              aria-selected={i === currentPage}
            />
          ))}
        </div>
      )}
    </>
  );
}

const SelectPosition = forwardRef<SelectPositionRef>((_, ref) => {
  const [open, setOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [positions, setPositions] = useState<PositionItem[]>([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [pointsModalOpen, setPointsModalOpen] = useState(false);
  const setNextMessageId = useSetRecoilState(nextMessageIdState);
  const setAllMessages = useSetRecoilState(allInterviewMessagesState);
  const resumeNumberRef = useRef<number | null>(null);
  const navigate = useNavigate();

  const loadPositions = async () => {
    try {
      setPositionsLoading(true);
      const list = await getInterviewPositions();
      setPositions(mapPositionDTOsToItems(list));
    } finally {
      setPositionsLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    show: (resumeNumber: number) => {
      resumeNumberRef.current = resumeNumber;
      setOpen(true);
      if (!positions.length) {
        void loadPositions();
      }
    },
  }));

  const handleSelectPosition = (positionNumber: number) => {
    setSelectedPosition(positionNumber);
  };

  const handleClose = () => {
    setOpen(false);
    window.location.hash = '';
  };

  const handleConfirm = async () => {
    if (selectedPosition && !loading && resumeNumberRef.current !== null) {
      try {
        setLoading(true);
        const { interviewNumber, nextMessageId } = await createInterview(
          selectedPosition,
          resumeNumberRef.current,
        );
        handleClose();

        setNextMessageId(nextMessageId);
        setAllMessages((prev) => ({
          ...prev,
          [interviewNumber]: [
            {
              role: 'assistant',
              messageId: nextMessageId,
              content: '',
              thinking: true,
              timestamp: Date.now(),
            },
          ],
        }));
        // 导航到面试页面
        navigate(`/interview/${interviewNumber}`);
      } catch (error) {
        if ((error as Error).message.includes('积分不足')) {
          // 关闭当前弹窗
          handleClose();
          // 打开积分和分享弹窗
          setPointsModalOpen(true);
        }
        // eslint-disable-next-line no-console
        console.error('创建面试失败:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <Modal
        open={open}
        footer={null}
        onCancel={handleClose}
        width="680px"
        closable
        styles={{
          content: {
            background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
            borderRadius: '20px',
          },
        }}
      >
        {/* Header Section */}
        <div className="text-center mb-8 pt-2">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/30 mb-4">
            <BulbOutlined className="text-2xl text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2 tracking-tight">选择面试岗位</h1>
          <p className="text-sm text-slate-500 leading-relaxed max-w-sm mx-auto">
            选择你想要面试的岗位，小灵将根据岗位特点进行针对性提问
          </p>
        </div>

        {/* Position Cards: 横向分页，每页 6 个 */}
        <SelectPositionStep
          positions={positions}
          selectedId={selectedPosition}
          onSelect={handleSelectPosition}
          loading={positionsLoading}
        />

        {/* Action Button */}
        <div className="flex gap-3 items-stretch mb-5">
          <Button
            type="primary"
            size="large"
            className="flex-1 h-12 rounded-xl font-medium text-base shadow-lg transition-all duration-300 hover:shadow-xl disabled:opacity-50"
            style={{
              background: selectedPosition
                ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                : undefined,
              borderColor: 'transparent',
            }}
            onClick={handleConfirm}
            disabled={!selectedPosition || loading}
            loading={loading}
          >
            {loading ? '正在创建...' : '开始面试'}
          </Button>
        </div>

        {/* Tips Section */}
        <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100/50 flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <BulbOutlined className="text-sm text-white" />
          </div>
          <p className="text-xs text-slate-600 leading-[32px]">
            <span className="font-medium text-slate-700">温馨提示：</span>
            面试真题实时更新，360 评估报告
          </p>
        </div>
      </Modal>

      {/* 积分不足时显示的积分和分享弹窗 */}
      <Modal
        open={pointsModalOpen}
        onCancel={() => {
          setPointsModalOpen(false);
        }}
        footer={null}
        width="750px"
        closable
      >
        <PointsGetter
          onPaySuccess={() => {
            setPointsModalOpen(false);
            handleConfirm();
          }}
        />
      </Modal>
    </>
  );
});

export default SelectPosition;
